import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SecurityTest from '../../components/SecurityTest';
import AuthTest from '../../components/AuthTest';
import FirestoreTest from '../../components/FirestoreTest';
import StorageTest from '../../components/StorageTest';
import APITest from '../../components/APITest';
import PerformanceTest from '../../components/PerformanceTest';
import ErrorTest from '../../components/ErrorTest';
import SEOTest from '../../components/SEOTest';
import AnalyticsTest from '../../components/AnalyticsTest';
import AccessibilityTest from '../../components/AccessibilityTest';
import BrowserTest from '../../components/BrowserTest';
import MobileTest from '../../components/MobileTest';
import SecurityHeadersTest from '../../components/SecurityHeadersTest';
import TestSummary from '../../components/TestSummary';
import DeploymentChecklist from '../../components/DeploymentChecklist';
import styled from 'styled-components';

const DashboardContainer = styled.div`
  padding: 2rem;
`;

const DashboardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const DashboardTitle = styled.h1`
  margin: 0;
`;

const DashboardActions = styled.div`
  display: flex;
  gap: 1rem;
`;

const DashboardButton = styled(Link)`
  background-color: var(--primary-color);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  text-decoration: none;
  transition: background-color 0.2s;

  &:hover {
    background-color: #333;
    color: white;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const StatTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
  color: #666;
`;

const StatValue = styled.p`
  margin: 0;
  font-size: 1.5rem;
  font-weight: bold;
  color: var(--primary-color);
`;

const SecuritySection = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-top: 2rem;
  overflow: hidden;
`;

const SecurityHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
  transition: background-color 0.2s;

  h2 {
    margin: 0;
    font-size: 1.25rem;
    color: var(--primary-color);
  }

  span {
    font-size: 0.8rem;
    color: #666;
  }

  &:hover {
    background-color: #f0f0f0;
  }
`;

const SecurityContent = styled.div`
  padding: 1.5rem;
  display: ${props => props.isOpen ? 'block' : 'none'};
  background-color: white;
`;

const AdminDashboard = () => {
  const [isSecurityOpen, setIsSecurityOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [isFirestoreOpen, setIsFirestoreOpen] = useState(false);
  const [isStorageOpen, setIsStorageOpen] = useState(false);
  const [isAPIOpen, setIsAPIOpen] = useState(false);
  const [isPerformanceOpen, setIsPerformanceOpen] = useState(false);
  const [isErrorOpen, setIsErrorOpen] = useState(false);
  const [isSEOOpen, setIsSEOOpen] = useState(false);
  const [isAnalyticsOpen, setIsAnalyticsOpen] = useState(false);
  const [isAccessibilityOpen, setIsAccessibilityOpen] = useState(false);
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSecurityHeadersOpen, setIsSecurityHeadersOpen] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(true);
  const [isChecklistOpen, setIsChecklistOpen] = useState(true);

  return (
    <DashboardContainer>
      <DashboardHeader>
        <DashboardTitle>Admin Dashboard</DashboardTitle>
        <DashboardActions>
          <DashboardButton to="/admin/posts">Manage Posts</DashboardButton>
        </DashboardActions>
      </DashboardHeader>

      <StatsGrid>
        <StatCard>
          <StatTitle>Total Posts</StatTitle>
          <StatValue>0</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Total Categories</StatTitle>
          <StatValue>0</StatValue>
        </StatCard>
        <StatCard>
          <StatTitle>Total Users</StatTitle>
          <StatValue>1</StatValue>
        </StatCard>
      </StatsGrid>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsSummaryOpen(!isSummaryOpen)}>
          <h2>Test Summary</h2>
          <span>{isSummaryOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isSummaryOpen}>
          <TestSummary />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsChecklistOpen(!isChecklistOpen)}>
          <h2>Deployment Checklist</h2>
          <span>{isChecklistOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isChecklistOpen}>
          <DeploymentChecklist />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsSecurityOpen(!isSecurityOpen)}>
          <h2>Security Tests</h2>
          <span>{isSecurityOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isSecurityOpen}>
          <SecurityTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsAuthOpen(!isAuthOpen)}>
          <h2>Authentication Tests</h2>
          <span>{isAuthOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isAuthOpen}>
          <AuthTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsFirestoreOpen(!isFirestoreOpen)}>
          <h2>Firestore Security Rules Tests</h2>
          <span>{isFirestoreOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isFirestoreOpen}>
          <FirestoreTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsStorageOpen(!isStorageOpen)}>
          <h2>Storage Security Rules Tests</h2>
          <span>{isStorageOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isStorageOpen}>
          <StorageTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsAPIOpen(!isAPIOpen)}>
          <h2>API Integration Tests</h2>
          <span>{isAPIOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isAPIOpen}>
          <APITest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsPerformanceOpen(!isPerformanceOpen)}>
          <h2>Performance Tests</h2>
          <span>{isPerformanceOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isPerformanceOpen}>
          <PerformanceTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsErrorOpen(!isErrorOpen)}>
          <h2>Error Handling Tests</h2>
          <span>{isErrorOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isErrorOpen}>
          <ErrorTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsSEOOpen(!isSEOOpen)}>
          <h2>SEO and Metadata Tests</h2>
          <span>{isSEOOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isSEOOpen}>
          <SEOTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsAnalyticsOpen(!isAnalyticsOpen)}>
          <h2>Analytics and Monitoring Tests</h2>
          <span>{isAnalyticsOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isAnalyticsOpen}>
          <AnalyticsTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsAccessibilityOpen(!isAccessibilityOpen)}>
          <h2>Accessibility Tests</h2>
          <span>{isAccessibilityOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isAccessibilityOpen}>
          <AccessibilityTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsBrowserOpen(!isBrowserOpen)}>
          <h2>Browser Compatibility Tests</h2>
          <span>{isBrowserOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isBrowserOpen}>
          <BrowserTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsMobileOpen(!isMobileOpen)}>
          <h2>Mobile Responsiveness Tests</h2>
          <span>{isMobileOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isMobileOpen}>
          <MobileTest />
        </SecurityContent>
      </SecuritySection>

      <SecuritySection>
        <SecurityHeader onClick={() => setIsSecurityHeadersOpen(!isSecurityHeadersOpen)}>
          <h2>Security Headers and Protection Tests</h2>
          <span>{isSecurityHeadersOpen ? '▼' : '▶'}</span>
        </SecurityHeader>
        <SecurityContent isOpen={isSecurityHeadersOpen}>
          <SecurityHeadersTest />
        </SecurityContent>
      </SecuritySection>
    </DashboardContainer>
  );
};

export default AdminDashboard; 